// ============================================================
// ANSICHTSSACHE-AGENT – Switch/Toggle Component
// ============================================================
// `hint` steht wie beim Kaestchen unter der Beschriftung an DEREN
// Textkante: die Erklaerung, was der Schalter bewirkt. Dann sitzt
// der Schalter auf der ERSTEN Textzeile statt in der Mitte des
// Blocks — 20 Pixel hoch neben einer 18 Pixel hohen Zeile, die
// Schrift deshalb einen Pixel tiefer. Vorgelesen wird der Satz als
// Beschreibung, nicht als Teil des Namens: der bleibt „zugelassen“.
// ============================================================

"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  /** Die Erklaerung zum Schalter — steht an der Textkante der Beschriftung. */
  hint?: React.ReactNode;
  /** Der Name fuer Vorleseprogramme, wo keine sichtbare Beschriftung daneben steht (Tabellenzelle). */
  "aria-label"?: string;
  disabled?: boolean;
  className?: string;
}

function Switch({
  checked,
  onChange,
  label,
  hint,
  "aria-label": ariaLabel,
  disabled,
  className,
}: SwitchProps) {
  const id = useId();
  const mitHinweis = Boolean(hint);

  return (
    <label
      className={cn(
        "inline-flex cursor-pointer gap-2",
        mitHinweis ? "items-start" : "items-center",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={mitHinweis && label && !ariaLabel ? `${id}-label` : undefined}
        aria-describedby={mitHinweis ? `${id}-hint` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "duration-fast relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors",
          checked ? "bg-accent" : "bg-bg-tertiary",
        )}
      >
        <span
          className={cn(
            "duration-fast pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-xs transition-transform",
            checked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </button>
      {mitHinweis ? (
        <span className="min-w-0 pt-px">
          {label && (
            <span id={`${id}-label`} className="text-body-sm block select-none">
              {label}
            </span>
          )}
          <span
            id={`${id}-hint`}
            className="text-caption text-text-tertiary mt-0.5 block select-none"
          >
            {hint}
          </span>
        </span>
      ) : (
        label && <span className="text-body-sm select-none">{label}</span>
      )}
    </label>
  );
}

export { Switch };
